
import { useSearchParams, useLocation } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BlogHeaderStyle from "../assets/stylesheets/blog/BlogHeader.module.css";
import { Logo } from "../assets/logo/Logo";
import Logger from "../utils/logger";
import { SearchLogo}  from '../assets/logo/Search';
import { ReactComponent as Close} from '../assets/icons/close.svg';
import SignInButton from "../components/auth/SignInButton";

const log = Logger.create('Header');

const TikTok = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M16.6 5.82C15.9 5.06 15.5 4.08 15.5 3H12.4V16.94C12.4 18.6797 11.0797 20 9.34 20C7.6003 20 6.28 18.6797 6.28 16.94C6.28 15.2003 7.6003 13.88 9.34 13.88C9.89 13.88 10.41 14.04 10.86 14.3V11.03C10.36 10.92 9.84 10.87 9.32 10.87C6.04 10.87 3.38 13.54 3.38 16.81C3.38 20.08 6.04 22.75 9.32 22.75C12.6 22.75 15.26 20.08 15.26 16.81V9.66C16.45 10.56 17.91 11.09 19.46 11.09V8C19.46 8 17.75 8.11 16.6 5.82Z"
      fill="currentColor"
    />
  </svg>
);


export default function BlogHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [categories, setCategories] = useState([]);
  const [scrollY, setScrollY] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);

  // Check if we're on the main blog page (list page)
  const isMainBlogPage = location.pathname === '/blog' || location.pathname === '/blog';
  
  // Get selected category from URL params, but only if we're on main blog page
  const selectedCategory = isMainBlogPage ? (searchParams.get('category') || 'all') : null;

  useEffect(() => {
    fetchCategories();
  }, []);

  // Sync searchInput with URL params
  useEffect(() => {
    const searchFromURL = searchParams.get('search') || '';
    setSearchInput(searchFromURL);
  }, [searchParams]);

  const fetchCategories = async () => {
    try {
      const blogCategories = [
        { id: 1, name: 'All', slug: 'all' },
        { id: 2, name: 'Experience', slug: 'experience' },
        { id: 3, name: 'Farming', slug: 'farming' },
        { id: 4, name: 'Travel', slug: 'travel' },
        { id: 5, name: 'Event', slug: 'event' },
      ];
      
      setCategories(blogCategories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  useEffect(() => {
    let ticking = false;
    let scrollTimeout;
    
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          
          // Set scrolled state immediately (for header height change)
          setIsScrolled(currentScrollY > 1);
          
          // Determine scroll direction with improved logic
          if (currentScrollY > lastScrollY && currentScrollY > 70) {
            // Only hide header if we're not at the top and scrolling down significantly
            // Add a small delay to prevent flickering
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
              // Check if we're still scrolling down
              if (window.scrollY >= currentScrollY) {
                setHeaderVisible(false);
              }
            }, 100);
          } else if (currentScrollY < lastScrollY) {
            // Always show header immediately when scrolling up
            setHeaderVisible(true);
          }
          
          setLastScrollY(currentScrollY);
          setScrollY(currentScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    const onResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      clearTimeout(scrollTimeout);
    };
  }, [lastScrollY]);

  // Apply appropriate class to main content container for transform
  useEffect(() => {
    const mainContainer = document.querySelector('main') || document.getElementById('root');
    
    if (mainContainer) {
      if (headerVisible) {
        mainContainer.classList.add(BlogHeaderStyle['header-visible']);
        mainContainer.classList.remove(BlogHeaderStyle['header-hidden']);
      } else {
        // Only add header-hidden if we're actually hidden and past the initial scroll threshold
        if (scrollY > 70) {
          mainContainer.classList.add(BlogHeaderStyle['header-hidden']);
          mainContainer.classList.remove(BlogHeaderStyle['header-visible']);
        }
      }
    }
  }, [headerVisible, scrollY]);
  
  const handleMobileSearchOpen = () => {
    setShowMobileSearch(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    // Trigger immediate dropdown display and focus
    setTimeout(() => {
      const searchInput = document.querySelector('.mobile-search-container input');
      if (searchInput) {
        // Trigger a focus event to ensure dropdown shows
        searchInput.focus();
        // Dispatch a custom event to force dropdown visibility
        searchInput.dispatchEvent(new Event('focus', { bubbles: true }));
      }
    }, 150);
  };

  const handleMobileSearchClose = () => {
    setShowMobileSearch(false);
    // Restore body scroll
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
  };

  // Close modal when switching to desktop
  useEffect(() => {
    if (!isMobile && showMobileSearch) {
      handleMobileSearchClose();
    }
  }, [isMobile, showMobileSearch]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    
    // Build new URL with current category and search params
    const params = new URLSearchParams();
    
    // Keep current category if exists
    const currentCategory = searchParams.get('category');
    if (currentCategory && currentCategory !== 'all') {
      params.set('category', currentCategory);
    }
    
    // Add search param if exists
    if (searchInput.trim()) {
      params.set('search', searchInput.trim());
    }
    
    // Navigate to /blog with params
    const queryString = params.toString();
    navigate(`/blog${queryString ? '?' + queryString : ''}`);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput('');
    
    // Keep current category but remove search
    const params = new URLSearchParams();
    const currentCategory = searchParams.get('category');
    if (currentCategory && currentCategory !== 'all') {
      params.set('category', currentCategory);
    }
    
    const queryString = params.toString();
    navigate(`/blog${queryString ? '?' + queryString : ''}`);
  };

  // Toggle side menu
  const toggleSideMenu = () => {
    setIsSideMenuOpen(!isSideMenuOpen);
  };

  // Close side menu when clicking overlay
  const handleOverlayClick = () => {
    setIsSideMenuOpen(false);
  };

  return (
    <>
      <header
        className={`${BlogHeaderStyle.header} ${BlogHeaderStyle["home-header"]} ${
          isScrolled ? BlogHeaderStyle.scrolled : ""
        } ${!headerVisible ? BlogHeaderStyle.hidden : ""}`}
      >
      <div 
        className={`${BlogHeaderStyle["header-container"]} ${BlogHeaderStyle["home-header-container"]}`}
      >
        <div className={BlogHeaderStyle["header-top"]}>
          {!isMobile && (
            <>
              <div className={BlogHeaderStyle["logo-container"]}>
                <a href="/" className={BlogHeaderStyle["logo-link"]}>
                  <Logo
                    className={BlogHeaderStyle["logo"]}
                    aria-label="Farmciti Logo"
                  />
                </a>
              </div>

              {/* Blog Categories - Center */}
              <nav className={BlogHeaderStyle["blog-categories"]}>
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      className={`${BlogHeaderStyle["category-btn"]}${
                        selectedCategory === cat.slug ? ' ' + BlogHeaderStyle["active"] : ''
                      }`}
                      onClick={() => {
                        // Navigate to /blog with category parameter
                        if (cat.slug === 'all') {
                          // Always navigate to /blog without category param for 'all'
                          navigate('/blog');
                        } else {
                          // Navigate to /blog with category param
                          navigate(`/blog?category=${cat.slug}`);
                        }
                      }}
                    >
                      {cat.name}
                    </button>
                  ))}
              </nav>
              
              {/* Search - Right */}
              <div className={BlogHeaderStyle["header-right"]}>
                <form onSubmit={handleSearchSubmit} className={BlogHeaderStyle["search-form"]}>
                  <div className={BlogHeaderStyle["search-container"]}>
                    <input
                      type="text"
                      value={searchInput}
                      onChange={handleSearchChange}
                      placeholder="Search posts..."
                      className={BlogHeaderStyle["search-input"]}
                    />

                    <button
                      type="submit"
                      className={BlogHeaderStyle["search-btn"]}
                      aria-label="Search"
                    >
                      <SearchLogo />
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
          
          {/* Mobile version */}
          {isMobile && (
            <>
              <div className={BlogHeaderStyle["logo-container"]}>
                <a href="/" className={BlogHeaderStyle["logo-link"]}>
                  <Logo
                    className={BlogHeaderStyle["logo"]}
                    aria-label="FarmCiti Logo"
                  />
                </a>
              </div>
              
              {/* Hamburger Menu Button */}
              <button
                className={BlogHeaderStyle["hamburger-menu-button"]}
                aria-label="Menu"
                onClick={toggleSideMenu}
              >
                <div className={`${BlogHeaderStyle["hamburger-icon"]} ${isSideMenuOpen ? BlogHeaderStyle["open"] : ""}`}>
                  <span className={BlogHeaderStyle["hamburger-line"]}></span>
                  <span className={BlogHeaderStyle["hamburger-line"]}></span>
                  <span className={BlogHeaderStyle["hamburger-line"]}></span>
                </div>
              </button>
            </>
          )}
        </div>

        {/* Mobile Search Bar - Below logo */}
        {isMobile && (
          <div className={BlogHeaderStyle["mobile-search-bar"]}>
            <form onSubmit={handleSearchSubmit} className={BlogHeaderStyle["search-form"]}>
              <div className={BlogHeaderStyle["search-container"]}>
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleSearchChange}
                  placeholder="Search posts..."
                  className={BlogHeaderStyle["search-input"]}
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className={BlogHeaderStyle["clear-btn"]}
                    aria-label="Clear search"
                  >
                    <Close />
                  </button>
                )}
                <button
                  type="submit"
                  className={BlogHeaderStyle["search-btn"]}
                  aria-label="Search"
                >
                  <SearchLogo />
                </button>
              </div>
            </form>
          </div>
        )}


      </div>
    </header>
  
    {/* Overlay */}
    {isSideMenuOpen && (
      <div 
        className={BlogHeaderStyle["menu-overlay"]}
        onClick={handleOverlayClick}
      />
    )}

    {/* Side Menu */}
    <div className={`${BlogHeaderStyle["side-menu"]} ${isSideMenuOpen ? BlogHeaderStyle["open"] : ""}`}>
      <div className={BlogHeaderStyle["side-menu-content"]}>
        <h3>Menu</h3>
        {/* Add menu content here */}
        <nav className={BlogHeaderStyle["side-menu-nav"]}>
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`${BlogHeaderStyle["side-menu-item"]}${
                selectedCategory === cat.slug ? ' ' + BlogHeaderStyle["active"] : ''
              }`}
              onClick={() => {
                if (cat.slug === 'all') {
                  navigate('/blog');
                } else {
                  navigate(`/blog?category=${cat.slug}`);
                }
                setIsSideMenuOpen(false);
              }}
            >
              {cat.name}
            </button>
          ))}
          
          <div className={BlogHeaderStyle["side-menu-divider"]} />
          
          <div className={BlogHeaderStyle["side-menu-auth"]}>
            <SignInButton inSideMenu={true} />
          </div>
        </nav>
      </div>
    </div>
    </>
  );
}
